import React, { useState, useEffect, useRef } from 'react';
import { thunksGetNews } from 'services/News/thunks';
import { selectorsGetNews } from 'services/News/selectors';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { createStructuredSelector } from 'reselect';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const NewsDetails = (props) => {
    const { intl } = props;
    const [groupedNews, setGroupedNews] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);
    const dateRefs = useRef({}); // refs for scrolling

    const categoryMap = {
        1: intl.formatMessage({ id: 'news.popup.overview' }),
        2: intl.formatMessage({ id: 'news.popup.history' }),
        3: intl.formatMessage({ id: 'news.popup.configurations' }),
        4: intl.formatMessage({ id: 'news.popup.notifications' }),
        5: intl.formatMessage({ id: 'news.popup.geofences' }),
        6: intl.formatMessage({ id: 'news.popup.reports' }),
        7: intl.formatMessage({ id: 'news.popup.dashboard' }),
        8: intl.formatMessage({ id: 'news.popup.administration' }),
        9: intl.formatMessage({ id: 'news.popup.maintenance' }),
        10: intl.formatMessage({ id: 'news.popup.documentation' }),
        11: intl.formatMessage({ id: 'news.popup.carpooling' }),
        12: intl.formatMessage({ id: 'news.popup.sizing' }),
    };

    const typeMap = {
        1: intl.formatMessage({ id: 'news.popup.features' }),
        2: intl.formatMessage({ id: 'news.popup.hotfix' }),
    };

    useEffect(() => {
        props.thunksGetNews();
    }, []);

    useEffect(
        () => {
            if (props.getNews && props.getNews.length > 0) {
                const articles = [...props.getNews].filter((article) => article.publish_date);

                const grouped = {};
                articles.forEach((article) => {
                    const date = new Date(article.publish_date).toLocaleDateString();
                    const validItems = (article.news || []).filter(
                        (item) =>
                            item &&
                            ((item.content_en && item.content_en !== 'string') ||
                                (item.content_fr && item.content_fr !== 'string') ||
                                (item.content_es && item.content_es !== 'string')) &&
                            item.category_id &&
                            item.news_type_id
                    );
                    if (validItems.length > 0) {
                        grouped[date] = grouped[date]
                            ? [...grouped[date], ...validItems]
                            : validItems;
                    }
                });

                const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));
                setGroupedNews(grouped);
                setSelectedDate(sortedDates[0] || null);
            }
        },
        [props.getNews]
    );

    const getContent = (item) => {
        switch (intl.locale) {
            case 'fr':
                return item.content_fr || item.content_en || '';
            case 'es':
                return item.content_es || item.content_en || '';
            default:
                return item.content_en || '';
        }
    };

    const scrollToDate = (date) => {
        setSelectedDate(date);
        const ref = dateRefs.current[date];
        if (ref && ref.scrollIntoView) {
            ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const renderNewsGroup = (date, items) => (
        <Box key={date} ref={(el) => (dateRefs.current[date] = el)} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {date}
            </Typography>
            <Divider sx={{ my: 2 }} />
            {items.map((item, index) => (
                <Box key={item.news_id || index} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {categoryMap[item.category_id] || 'Unknown Category'}
                        </Typography>
                        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                            {typeMap[item.news_type_id] || 'Unknown Type'}
                        </Typography>
                    </Box>
                    <Typography variant="body1">{getContent(item)}</Typography>
                    {index < items.length - 1 && <Divider sx={{ my: 2 }} />}
                </Box>
            ))}
            <Divider sx={{ my: 3 }} />
        </Box>
    );

    const sortedDates = Object.keys(groupedNews).sort((a, b) => new Date(b) - new Date(a));

    return (
        <Box sx={{ display: 'flex', position: 'relative', height: '100%', minHeight: '400px' }}>
            {/* Left Side: Dates List */}
            <Box sx={{ width: '30%', p: 2, borderRight: '2px solid #ccc', overflowY: 'auto' }}>
                <Typography variant="h6" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
                    {intl.formatMessage({ id: 'news.popup.lastestupdates' })}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {sortedDates.length > 0 ? (
                    sortedDates.map((date) => (
                        <Box
                            key={date}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                                mb: 1,
                                borderRadius: 1,
                                bgcolor: selectedDate === date ? 'success.light' : 'transparent',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                            onClick={() => scrollToDate(date)}
                        >
                            <Typography variant="body2">{date}</Typography>
                            <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                        </Box>
                    ))
                ) : (
                    <Typography variant="body2" align="center" color="text.secondary">
                        {intl.formatMessage({ id: 'news.popup.noupdatesavailable' })}
                    </Typography>
                )}
            </Box>

            {/* Right Side: News Items Grouped by Date */}
            <Box sx={{ width: '70%', p: 3, overflowY: 'auto' }}>
                {sortedDates.length > 0 ? (
                    sortedDates.map((date) => renderNewsGroup(date, groupedNews[date]))
                ) : (
                    <Typography variant="body2" align="center" color="text.secondary">
                        {intl.formatMessage({ id: 'news.popup.noupdatesavailable' })}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const mapStateToProps = createStructuredSelector({
    getNews: selectorsGetNews(),
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            thunksGetNews,
        },
        dispatch
    );

export default withRouter(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(injectIntl(NewsDetails))
);
